import React from "react";
import { Item } from "../pages/AddActivity";
import { Customer, BankDetail } from "../lib/api";
import { format } from "date-fns";
import { ScrollArea } from "./ui/scroll-area";
import SidePanel, { PanelHeader } from "./side-panel";
import ScaledPreview from "./scaled-preview";
import { useIsMobile } from "../hooks/use-is-mobile";
import {
  ColumnDef,
  evaluateFormula,
  getLineTotalFormulaColumn,
} from "../lib/quote-invoice-columns";

const DEFAULT_DESCRIPTION = "We are pleased to quote you for the following";

/** Single string for description display: "Description" or "Description 6" (matches Create form). */
function descriptionWithLength(
  description?: string,
  length?: string | number,
): string {
  const d = (description ?? "").trim();
  const hasLength =
    length != null && length !== "" && String(length) !== "undefined";
  return hasLength ? `${d} ${length}`.trim() : d;
}

interface QuotationPreviewProps {
  customer?: Customer;
  items: Item[];
  issueDate?: Date;
  expiryDate?: Date;
  title?: string;
  notes?: string;
  reference?: string;
  /** Introductory text above the items table. */
  description?: string;
  onClose: () => void;
  isOpen: boolean;
  isVatIncluded: boolean;
  showIsVatExempted?: boolean;
  currency: string;
  bankDetail?: BankDetail;
  /** When provided, table uses these columns and visibility (reflects Create form config). */
  columns?: ColumnDef[];
  columnVisibility?: Record<string, boolean>;
}

const DEFAULT_COLUMN_VISIBILITY: Record<string, boolean> = {
  description: true,
  unit: true,
  quantity: true,
  unitPrice: true,
  total: true,
};

const QuotationPreview: React.FC<QuotationPreviewProps> = ({
  customer,
  items,
  issueDate,
  expiryDate,
  title,
  notes,
  reference,
  onClose,
  isOpen,
  isVatIncluded,
  showIsVatExempted = false,
  currency,
  bankDetail,
  description: descriptionProp,
  columns: columnsProp,
  columnVisibility: columnVisibilityProp,
}) => {
  const isMobile = useIsMobile();
  const description = descriptionProp ?? DEFAULT_DESCRIPTION;

  const useDynamicColumns = Boolean(
    columnsProp?.length && columnVisibilityProp,
  );
  const columns = columnsProp ?? [];
  const columnVisibility = useDynamicColumns
    ? columnVisibilityProp!
    : DEFAULT_COLUMN_VISIBILITY;

  const getRowForFormula = (item: any, index: number) => {
    const qty = Number(item.rental_quantity ?? item.quantity ?? 0) || 0;
    const unitPrice = Number(item.unit_price ?? item.unitPrice ?? 0) || 0;
    const lineTotal = qty * unitPrice;
    const discountType = String(
      (item.data?.discountType as string) ?? "None",
    ).trim();
    const discountVal = Number(item.data?.discount) || 0;
    const discountAmount =
      discountType === "Fixed"
        ? discountVal
        : discountType === "Percentage"
          ? Math.round(((lineTotal * discountVal) / 100) * 100) / 100
          : 0;
    return {
      ...(item.data ?? {}),
      sno: index + 1,
      description: item.description ?? "",
      unit: item.unit ?? "",
      qty,
      unitPrice,
      discountAmount,
    };
  };

  const getRowAmount = (item: any, index: number): number => {
    let amount: number;
    if (useDynamicColumns && columns.length) {
      const formulaCol = getLineTotalFormulaColumn(columns);
      if (formulaCol?.formula) {
        amount = evaluateFormula(
          formulaCol.formula,
          getRowForFormula(item, index),
          columns,
        );
      } else {
        amount =
          (item.rental_quantity ?? item.quantity ?? 0) *
          (item.unit_price ?? item.unitPrice ?? 0);
      }
    } else {
      amount =
        (item.rental_quantity ?? item.quantity ?? 0) *
        (item.unit_price ?? item.unitPrice ?? 0);
    }
    return Math.round(amount * 100) / 100;
  };

  const getCellValue = (item: any, colId: string): string | number => {
    if (colId === "sno") return 0;
    if (colId === "description")
      return (
        descriptionWithLength(item.description, item.length) ||
        (item.item_code ?? "")
      );
    if (colId === "unit") return item.unit ?? "";
    if (colId === "qty") return item.rental_quantity ?? item.quantity ?? 0;
    if (colId === "unitPrice") return item.unit_price ?? item.unitPrice ?? 0;
    if (colId === "discountType") return item.data?.discountType ?? "None";
    if (colId === "discount") return item.data?.discount ?? 0;
    const v = item.data?.[colId];
    return v !== undefined && v !== "" ? v : "";
  };

  const subtotal =
    Math.round(
      items.reduce((total, item: any, i) => total + getRowAmount(item, i), 0) *
        100,
    ) / 100;

  const vat = showIsVatExempted
    ? 0
    : isVatIncluded
      ? Math.round(subtotal * 0.18 * 100) / 100
      : 0;
  const total = Math.round((subtotal + vat) * 100) / 100;

  const visibleColumns = useDynamicColumns
    ? columns.filter((c) => columnVisibility[c.id] !== false)
    : null;

  const formulaCols =
    visibleColumns?.filter((c) => c.type === "formula" && c.formula) ?? [];
  const formulaSums =
    formulaCols.length > 0
      ? formulaCols.map(
          (col) =>
            Math.round(
              items.reduce(
                (sum, item, i) =>
                  sum +
                  evaluateFormula(
                    col.formula!,
                    getRowForFormula(item, i),
                    columns,
                  ),
                0,
              ) * 100,
            ) / 100,
        )
      : [];

  const lineTotalFormulaId = getLineTotalFormulaColumn(columns)?.id;

  const getDescriptionDisplay = (item: any) =>
    item.is_bundle && item.bundle_items
      ? "Bundle: " + descriptionWithLength(item.description, item.length)
      : descriptionWithLength(item.description, item.length) ||
        (item.item_code ?? "");

  return (
    <SidePanel
      open={isOpen}
      onClose={onClose}
      width="w-[1000px]"
      layer="overlay"
    >
      {isOpen && (
        <div className="relative h-full flex flex-col">
          <PanelHeader
            title={`Quotation Preview${reference ? ` - ${reference}` : ""}`}
            onClose={onClose}
          />
          <div className="flex-grow overflow-hidden">
            <PanelScroll isMobile={isMobile}>
              <div className="w-full p-4 md:p-6 bg-gray-50">
                <ScaledPreview>
                  <div className="p-10 bg-white shadow-lg rounded-lg">
                    <div className="flex items-start justify-between mb-8">
                      <h1 className="text-3xl font-bold">{title ?? "Quote"}</h1>
                      <img
                        src="/images/logo.png"
                        alt="Company Logo"
                        className="w-80"
                      />
                    </div>

                    <section className="flex flex-row gap-4 text-sm">
                      <div className="flex-1">
                        <h1 className="text-base font-semibold">
                          {customer?.fullName}
                        </h1>
                        <p>
                          {customer?.postalCode
                            ? `P.O.BOX ${customer.postalCode}, `
                            : ""}
                          {customer?.street ? `${customer.street},` : ""}
                        </p>
                        <p>
                          {customer?.ward ? `${customer.ward}, ` : ""}
                          {customer?.district ? `${customer.district}.` : ""}
                        </p>
                        <p>
                          {customer?.region ? `${customer.region}, ` : ""}
                          {customer?.country ? `${customer.country}.` : ""}
                        </p>
                        {customer?.phoneNumber && (
                          <p>Phone: {customer?.phoneNumber}</p>
                        )}
                        <p>{customer?.vrn ? `VRN: ${customer.vrn}` : ""}</p>
                        <p>{customer?.tin ? `TIN: ${customer.tin}` : ""}</p>
                      </div>
                      <div>
                        {issueDate && (
                          <p className="text-sm">
                            <strong>Issue date</strong>
                            <br /> {format(issueDate, "dd-MM-yyyy")}
                          </p>
                        )}
                        <br />
                        {expiryDate && (
                          <p className="text-sm">
                            <strong>Expiry Date</strong>
                            <br /> {format(expiryDate, "dd-MM-yyyy")}
                          </p>
                        )}
                        {reference && (
                          <>
                            <br />
                            <p className="text-sm">
                              <strong>Reference</strong>
                              <br /> {reference}
                            </p>
                          </>
                        )}
                      </div>
                      <div className="block border-l border-gray-800" />
                      <div>
                        <h1 className="text-base font-semibold">
                          WAPO SCAFFOLDING (T) LIMITED
                        </h1>
                        <p>
                          Wazo Hill Tegeta,
                          <br />
                          Plot No. 161 Block C,
                        </p>
                        <p>
                          P.O Box 35854,
                          <br />
                          Dar es Salaam, Tanzania
                        </p>
                        <br />
                        <p>TIN: 124-154-243</p>
                        <p>VRN: 40-028187-U</p>
                        <br />
                        <p>Email: info@waposcaffolding.com</p>
                        <p>Website: www.waposcaffolding.co.tz</p>
                        <br />
                        <p>Phone: +255 (0) 716 029 263</p>
                      </div>
                    </section>

                    <h2 className="mt-10 mb-4 text-lg font-semibold">
                      {description}
                    </h2>

                    <div className="overflow-x-auto">
                      <table className="min-w-full text-sm">
                        <thead>
                          <tr>
                            {visibleColumns ? (
                              visibleColumns.map((col) => (
                                <th
                                  key={col.id}
                                  className={
                                    col.id === "description"
                                      ? "border border-gray-300 px-4 py-2 text-start"
                                      : col.type === "formula"
                                        ? "border border-gray-300 px-4 py-2 text-end tabular-nums min-w-[7rem]"
                                        : "border border-gray-300 px-4 py-2"
                                  }
                                >
                                  {col.label}
                                </th>
                              ))
                            ) : (
                              <>
                                <th className="border border-gray-300 px-4 py-2">
                                  #
                                </th>
                                {columnVisibility.description && (
                                  <th className="border border-gray-300 px-4 py-2 text-start">
                                    Description
                                  </th>
                                )}
                                {columnVisibility.unit && (
                                  <th className="border border-gray-300 px-4 py-2">
                                    Unit
                                  </th>
                                )}
                                {columnVisibility.quantity && (
                                  <th className="border border-gray-300 px-4 py-2">
                                    Qty
                                  </th>
                                )}
                                {columnVisibility.unitPrice && (
                                  <th className="border border-gray-300 px-4 py-2">
                                    Unit Price
                                  </th>
                                )}
                                {columnVisibility.total && (
                                  <th className="border border-gray-300 px-4 py-2 text-end">
                                    Total
                                  </th>
                                )}
                              </>
                            )}
                          </tr>
                        </thead>
                        <tbody className="text-center">
                          {items.map((item: any, index) =>
                            visibleColumns ? (
                              <tr key={index}>
                                {visibleColumns.map((col) => {
                                  const isDesc = col.id === "description";
                                  const isEnd = col.type === "formula";
                                  let content: React.ReactNode;
                                  if (col.id === "sno") {
                                    content = index + 1;
                                  } else if (
                                    col.type === "formula" &&
                                    col.formula
                                  ) {
                                    const row = getRowForFormula(item, index);
                                    const val = evaluateFormula(
                                      col.formula,
                                      row,
                                      columns,
                                    );
                                    content = Number.isFinite(val)
                                      ? val.toLocaleString(undefined, {
                                          minimumFractionDigits: 2,
                                          maximumFractionDigits: 2,
                                        })
                                      : String(val ?? "");
                                  } else if (col.id === "description") {
                                    content = getDescriptionDisplay(item);
                                  } else {
                                    const v = getCellValue(item, col.id);
                                    content =
                                      typeof v === "number"
                                        ? (v as number).toLocaleString(
                                            undefined,
                                            {
                                              minimumFractionDigits: 2,
                                              maximumFractionDigits: 2,
                                            },
                                          )
                                        : v;
                                  }
                                  return (
                                    <td
                                      key={col.id}
                                      className={
                                        isDesc
                                          ? "border-x border-gray-300 text-start px-4 py-2"
                                          : isEnd
                                            ? "border-x border-gray-300 px-4 py-2 text-end tabular-nums min-w-[7rem]"
                                            : "border-x border-gray-300 px-4 py-2"
                                      }
                                    >
                                      {content}
                                    </td>
                                  );
                                })}
                              </tr>
                            ) : (
                              <tr key={index}>
                                <td className="border-x border-gray-300 px-4 py-2">
                                  {index + 1}
                                </td>
                                {columnVisibility.description && (
                                  <td className="border-x border-gray-300 text-start px-4 py-2">
                                    {getDescriptionDisplay(item)}
                                  </td>
                                )}
                                {columnVisibility.unit && (
                                  <td className="border-x border-gray-300 px-4 py-2">
                                    {item.unit || "Month"}
                                  </td>
                                )}
                                {columnVisibility.quantity && (
                                  <td className="border-x border-gray-300 px-4 py-2">
                                    {item.rental_quantity || 0}
                                  </td>
                                )}
                                {columnVisibility.unitPrice && (
                                  <td className="border-x border-gray-300 px-4 py-2">
                                    {Number(
                                      item.unit_price || 0,
                                    ).toLocaleString(undefined, {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    })}
                                  </td>
                                )}
                                {columnVisibility.total && (
                                  <td className="border-x border-gray-300 px-4 py-2 text-end">
                                    {(
                                      (item.rental_quantity || 0) *
                                      (item.unit_price || 0)
                                    ).toLocaleString(undefined, {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    })}
                                  </td>
                                )}
                              </tr>
                            ),
                          )}

                          {visibleColumns && formulaCols.length > 0 ? (
                            <>
                              {formulaCols.length > 1 && (
                                <tr className="border-t border-gray-300">
                                  {visibleColumns.map((col, i) => {
                                    const isDesc = col.id === "description";
                                    const isFormula =
                                      col.type === "formula" && col.formula;
                                    const totalLabelCol = Math.max(
                                      0,
                                      visibleColumns.length -
                                        formulaCols.length -
                                        1,
                                    );
                                    let content: React.ReactNode = "";
                                    if (isFormula) {
                                      if (col.id === lineTotalFormulaId) {
                                        content = "";
                                      } else {
                                        const idx = formulaCols.findIndex(
                                          (fc) => fc.id === col.id,
                                        );
                                        const sum =
                                          idx >= 0 ? formulaSums[idx] : 0;
                                        content = sum.toLocaleString(
                                          undefined,
                                          {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                          },
                                        );
                                      }
                                    } else if (i === totalLabelCol) {
                                      content = "";
                                    }
                                    return (
                                      <td
                                        key={col.id}
                                        className={
                                          isDesc
                                            ? "border border-gray-300 text-start px-4 py-2 font-semibold"
                                            : isFormula
                                              ? "border border-gray-300 px-4 py-2 font-semibold text-end tabular-nums min-w-[7rem]"
                                              : "border border-gray-300 px-4 py-2 font-semibold"
                                        }
                                      >
                                        {content}
                                      </td>
                                    );
                                  })}
                                </tr>
                              )}
                              <tr
                                className={
                                  formulaCols.length > 1
                                    ? undefined
                                    : "border-t border-gray-300"
                                }
                              >
                                <td
                                  colSpan={visibleColumns.length - 1}
                                  className="text-end px-4 py-2 font-semibold"
                                >
                                  Sub-total
                                </td>
                                <td className="border border-gray-300 px-4 py-2 font-semibold text-end">
                                  {currency}{" "}
                                  {subtotal.toLocaleString(undefined, {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}
                                </td>
                              </tr>
                            </>
                          ) : (
                            <tr className="border-t border-gray-300">
                              <td
                                colSpan={
                                  visibleColumns
                                    ? visibleColumns.length - 1
                                    : 1 +
                                      (columnVisibility.description ? 1 : 0) +
                                      (columnVisibility.unit ? 1 : 0) +
                                      (columnVisibility.quantity ? 1 : 0) +
                                      (columnVisibility.unitPrice ? 1 : 0)
                                }
                                className="text-end px-4 py-2 font-semibold"
                              >
                                Sub-total
                              </td>
                              <td className="border border-gray-300 px-4 py-2 font-semibold text-end">
                                {currency}{" "}
                                {subtotal.toLocaleString(undefined, {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}
                              </td>
                            </tr>
                          )}

                          {showIsVatExempted ? (
                            <tr>
                              <td
                                colSpan={
                                  visibleColumns
                                    ? visibleColumns.length - 1
                                    : 1 +
                                      (columnVisibility.description ? 1 : 0) +
                                      (columnVisibility.unit ? 1 : 0) +
                                      (columnVisibility.quantity ? 1 : 0) +
                                      (columnVisibility.unitPrice ? 1 : 0)
                                }
                                className="text-end px-4 py-2 font-semibold"
                              >
                                VAT Exempt
                              </td>
                              <td className="border border-gray-300 px-4 py-2 font-semibold text-end">
                                —
                              </td>
                            </tr>
                          ) : isVatIncluded ? (
                            <tr>
                              <td
                                colSpan={
                                  visibleColumns
                                    ? visibleColumns.length - 1
                                    : 1 +
                                      (columnVisibility.description ? 1 : 0) +
                                      (columnVisibility.unit ? 1 : 0) +
                                      (columnVisibility.quantity ? 1 : 0) +
                                      (columnVisibility.unitPrice ? 1 : 0)
                                }
                                className="text-end px-4 py-2 font-semibold"
                              >
                                VAT (18%)
                              </td>
                              <td className="border border-gray-300 px-4 py-2 font-semibold text-end">
                                {currency}{" "}
                                {vat.toLocaleString(undefined, {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}
                              </td>
                            </tr>
                          ) : null}

                          <tr>
                            <td
                              colSpan={
                                visibleColumns
                                  ? visibleColumns.length - 1
                                  : 1 +
                                    (columnVisibility.description ? 1 : 0) +
                                    (columnVisibility.unit ? 1 : 0) +
                                    (columnVisibility.quantity ? 1 : 0) +
                                    (columnVisibility.unitPrice ? 1 : 0)
                              }
                              className="text-end px-4 py-2 font-bold"
                            >
                              Total
                            </td>
                            <td className="border border-gray-300 px-4 py-2 font-bold text-end">
                              {currency}{" "}
                              {total.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    {notes && (
                      <div className="mt-4">
                        <h2 className="text-md font-semibold mb-1">Note</h2>
                        <div className="whitespace-pre-wrap text-sm">
                          {notes}
                        </div>
                      </div>
                    )}

                    <section className="mt-10 text-sm">
                      <h2 className="text-md font-semibold mb-1">
                        BANK INFORMATION
                      </h2>
                      {bankDetail ? (
                        <p>
                          <strong>BANK NAME:</strong> {bankDetail.bankName}
                          <br />
                          <strong>ACCOUNT NAME:</strong>{" "}
                          {bankDetail.accountName}
                          <br />
                          <strong>ACCOUNT NO:</strong>{" "}
                          {bankDetail.accountNumber}
                          <br />
                          <strong>SWIFT CODE:</strong> {bankDetail.swiftCode}
                          <br />
                          <strong>BRANCH:</strong> {bankDetail.branchName}
                        </p>
                      ) : (
                        <p className="text-gray-600">
                          No bank information provided.
                        </p>
                      )}
                    </section>
                  </div>
                </ScaledPreview>
              </div>
            </PanelScroll>
          </div>
        </div>
      )}
    </SidePanel>
  );
};

const PanelScroll: React.FC<{
  isMobile: boolean;
  children: React.ReactNode;
}> = ({ isMobile, children }) =>
  isMobile ? (
    <div className="h-full overflow-y-auto overscroll-contain">{children}</div>
  ) : (
    <ScrollArea className="h-full">{children}</ScrollArea>
  );

export default QuotationPreview;
